/**
 * TagPill Component (Story 3.6)
 *
 * Renders a colored tag pill with auto-contrasting text.
 * Optional "x" button for removal (when onRemove is provided).
 */

interface TagPillProps {
  tag: { id: string; name: string; color: string };
  onRemove?: () => void;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Calculate contrasting text color based on background hex.
 * Uses ITU-R BT.709 perceived luminance.
 */
export function getContrastTextColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance > 128 ? "#1a1a1a" : "#ffffff";
}

export function TagPill({ tag, onRemove, size = "sm", className }: TagPillProps) {
  const textColor = "#ffffff";
  const sizeClasses = size === "sm" ? "text-xs px-3 py-1" : "text-sm px-3.5 py-1.5";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses} ${className ?? ""}`}
      style={{ backgroundColor: tag.color, color: textColor }}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-current cursor-pointer"
          style={{ minWidth: 0, minHeight: 0 }}
          aria-label={`Remove tag ${tag.name}`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </span>
  );
}
